import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  useTheme,
  styled,
  alpha,
  Grid,
  CircularProgress,
  LinearProgress,
  keyframes,
} from '@mui/material';
import {
  Science,
  Engineering,
  LocalShipping,
  VerifiedUser,
  Inventory2,
  Psychology,
  Whatshot,
  Diamond,
  AutoAwesome,
  Speed,
  TrendingUp,
  CheckCircle,
  AccessTime,
  Construction,
  Terrain,
  Layers,
  Hardware,
  Memory,
  Timeline,
  AccountTree,
  DeviceHub,
  Hub,
} from '@mui/icons-material';
import { samplesService } from '../services/samplesService';

// Stunning Animations
const networkPulse = keyframes`
  0% { 
    transform: scale(1); 
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.3);
  }
  50% { 
    transform: scale(1.05); 
    box-shadow: 0 0 40px rgba(251, 191, 36, 0.8);
  }
  100% { 
    transform: scale(1); 
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.3);
  }
`;

const flowAnimation = keyframes`
  0% { stroke-dashoffset: 100; opacity: 0.5; }
  50% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.8; }
`;

const particleFlow = keyframes`
  0% { transform: translateX(-10px) scale(0); opacity: 0; }
  10% { transform: translateX(0) scale(1); opacity: 1; }
  90% { transform: translateX(100px) scale(1); opacity: 1; }
  100% { transform: translateX(110px) scale(0); opacity: 0; }
`;

// Professional TV Container
const TVContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  height: '100vh',
  background: `
    radial-gradient(ellipse at top left, ${alpha('#1e293b', 0.8)} 0%, transparent 50%),
    radial-gradient(ellipse at top right, ${alpha('#334155', 0.6)} 0%, transparent 50%),
    radial-gradient(ellipse at bottom center, ${alpha('#0f172a', 0.9)} 0%, transparent 50%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 75%, #475569 100%)
  `,
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'Inter', 'Roboto', sans-serif",
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 80%, ${alpha('#fbbf24', 0.03)} 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, ${alpha('#3b82f6', 0.03)} 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, ${alpha('#dc2626', 0.02)} 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },
}));

// Network Visualization Container
const NetworkContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  background: `
    linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.02)} 0%, 
      ${alpha('#1e293b', 0.05)} 50%, 
      ${alpha(theme.palette.background.paper, 0.02)} 100%
    )
  `,
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  border: `1px solid ${alpha('#fbbf24', 0.1)}`,
  backdropFilter: 'blur(20px)',
  boxShadow: `
    inset 0 1px 0 ${alpha('#fbbf24', 0.1)},
    0 20px 40px ${alpha('#000', 0.3)}
  `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Centered Network Wrapper with responsive scaling
const NetworkWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '1800px',
  height: '600px',
  margin: '0 auto',
  transform: 'scale(1)',
  transformOrigin: 'center center',
  '@media (max-width: 1920px)': {
    transform: 'scale(0.9)',
  },
  '@media (max-width: 1600px)': {
    transform: 'scale(0.75)',
  },
  '@media (max-width: 1400px)': {
    transform: 'scale(0.65)',
  },
}));

// Professional Node Card
const NetworkNode = styled(Paper)<{ 
  nodeColor: string; 
  isActive?: boolean; 
  intensity?: number;
  nodeType?: string;
}>(({ theme, nodeColor, isActive, intensity = 0, nodeType }) => ({
  position: 'absolute',
  width: nodeType === 'main' ? 180 : 160,
  height: nodeType === 'main' ? 140 : 120,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: `
    linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.95)} 0%, 
      ${alpha(theme.palette.background.paper, 0.85)} 100%
    )
  `,
  backdropFilter: 'blur(30px)',
  border: `2px solid ${nodeColor}`,
  borderRadius: theme.spacing(2.5),
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `
    0 8px 32px ${alpha(nodeColor, 0.2)},
    inset 0 1px 0 ${alpha('#fff', 0.1)},
    0 0 0 ${alpha(nodeColor, 0.1)}
  `,
  '&:hover': {
    transform: 'translateY(-12px) scale(1.08)',
    boxShadow: `
      0 25px 50px ${alpha(nodeColor, 0.4)},
      0 0 30px ${alpha(nodeColor, 0.6)},
      inset 0 1px 0 ${alpha('#fff', 0.2)}
    `,
    border: `2px solid ${nodeColor}`,
    '& .node-glow': {
      opacity: 1,
    },
  },
  ...(isActive && {
    animation: `${networkPulse} 3s infinite`,
    boxShadow: `
      0 12px 40px ${alpha(nodeColor, 0.4)},
      0 0 20px ${alpha(nodeColor, 0.6)},
      inset 0 1px 0 ${alpha('#fff', 0.15)}
    `,
  }),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    background: `linear-gradient(45deg, ${nodeColor}, transparent, ${nodeColor})`,
    borderRadius: theme.spacing(2.5),
    zIndex: -1,
    opacity: isActive ? 0.3 : 0,
    transition: 'opacity 0.3s ease',
  },
}));

// Flowing Connection Lines
const ConnectionSVG = styled('svg')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 1,
});

const FlowPath = styled('path')<{ isActive?: boolean; flowIntensity?: number }>(({ isActive, flowIntensity = 0 }) => ({
  fill: 'none',
  strokeWidth: 3 + (flowIntensity * 2),
  strokeDasharray: '12 8',
  strokeLinecap: 'round',
  opacity: 0.7,
  filter: isActive ? 'drop-shadow(0 0 8px currentColor)' : 'none',
  animation: isActive ? `${flowAnimation} 3s infinite linear` : 'none',
  transition: 'all 0.3s ease',
}));

// Sample Count Badge
const CountBadge = styled(Box)<{ badgeColor: string; count: number }>(({ theme, badgeColor, count }) => ({
  position: 'absolute',
  top: -15,
  right: -15,
  minWidth: count > 99 ? 42 : 36,
  height: 36,
  borderRadius: '50%',
  background: `
    linear-gradient(135deg, ${badgeColor} 0%, ${alpha(badgeColor, 0.8)} 100%)
  `,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: count > 99 ? '12px' : '14px',
  fontWeight: 'bold',
  boxShadow: `
    0 6px 20px ${alpha(badgeColor, 0.6)},
    inset 0 1px 0 ${alpha('#fff', 0.3)}
  `,
  border: '3px solid rgba(255,255,255,0.2)',
  zIndex: 10,
  animation: count > 0 ? `${networkPulse} 2s infinite` : 'none',
}));

// Flowing Particles
const FlowParticle = styled(Box)<{ particleColor: string }>(({ particleColor }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${particleColor} 0%, ${alpha(particleColor, 0.8)} 70%, transparent 100%)`,
  boxShadow: `0 0 12px ${particleColor}`,
  animation: `${particleFlow} 4s infinite linear`,
  zIndex: 5,
}));

// Statistics Card
const StatsCard = styled(Paper)(({ theme }) => ({
  background: `
    linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.95)} 0%, 
      ${alpha(theme.palette.background.paper, 0.85)} 100%
    )
  `,
  backdropFilter: 'blur(30px)',
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${alpha('#fbbf24', 0.2)}`,
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `
    0 8px 32px ${alpha('#000', 0.1)},
    inset 0 1px 0 ${alpha('#fbbf24', 0.1)}
  `,
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: `
      0 20px 40px ${alpha('#fbbf24', 0.15)},
      inset 0 1px 0 ${alpha('#fbbf24', 0.2)}
    `,
    border: `1px solid ${alpha('#fbbf24', 0.4)}`,
  },
}));

interface NetworkNodeData {
  id: string;
  name: string;
  shortName: string;
  icon: React.ReactElement;
  x: number;
  y: number;
  color: string;
  count: number;
  isActive: boolean;
  laboratory?: string;
  nodeType?: string;
  connections: string[];
}

interface FlowingParticle {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  color: string;
  delay: number;
}

const TVDashboard: React.FC = () => {
  const theme = useTheme();
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [particles, setParticles] = useState<FlowingParticle[]>([]);
  const networkRef = useRef<HTMLDivElement>(null);

  // Network nodes with perfect positioning for TV
  const [networkNodes, setNetworkNodes] = useState<NetworkNodeData[]>([
    {
      id: 'reception',
      name: 'Réception Minerai',
      shortName: 'RÉCEPTION',
      icon: <Terrain fontSize="large" />,
      x: 100,
      y: 300,
      color: '#8b5a3c',
      count: 0,
      isActive: false,
      nodeType: 'main',
      connections: ['preparation'],
    },
    {
      id: 'preparation',
      name: 'Préparation Mécanique',
      shortName: 'PRÉPARATION',
      icon: <Construction fontSize="large" />,
      x: 350,
      y: 300,
      color: '#6b7280',
      count: 0,
      isActive: false,
      nodeType: 'main',
      connections: ['saa_prep', 'icp_prep', 'au_prep'],
    },
    {
      id: 'saa_prep',
      name: 'SAA Préparation',
      shortName: 'SAA-P',
      icon: <Layers fontSize="large" />,
      x: 600,
      y: 150,
      color: '#dc2626',
      count: 0,
      isActive: false,
      laboratory: 'SAA',
      connections: ['saa_analysis'],
    },
    {
      id: 'saa_analysis',
      name: 'SAA Analyse',
      shortName: 'SAA-A',
      icon: <Whatshot fontSize="large" />,
      x: 800,
      y: 150,
      color: '#dc2626',
      count: 0,
      isActive: false,
      laboratory: 'SAA',
      connections: ['validation'],
    },
    {
      id: 'icp_prep',
      name: 'ICP Préparation',
      shortName: 'ICP-P',
      icon: <Hardware fontSize="large" />,
      x: 600,
      y: 300,
      color: '#0891b2',
      count: 0,
      isActive: false,
      laboratory: 'ICP',
      connections: ['icp_analysis'],
    },
    {
      id: 'icp_analysis',
      name: 'ICP Analyse',
      shortName: 'ICP-A',
      icon: <Memory fontSize="large" />,
      x: 800,
      y: 300,
      color: '#0891b2',
      count: 0,
      isActive: false,
      laboratory: 'ICP',
      connections: ['validation'],
    },
    {
      id: 'au_prep',
      name: 'AU Préparation',
      shortName: 'AU-P',
      icon: <Diamond fontSize="large" />,
      x: 600,
      y: 450,
      color: '#f59e0b',
      count: 0,
      isActive: false,
      laboratory: 'AU',
      connections: ['au_analysis'],
    },
    {
      id: 'au_analysis',
      name: 'AU Analyse',
      shortName: 'AU-A',
      icon: <AutoAwesome fontSize="large" />,
      x: 800,
      y: 450,
      color: '#f59e0b',
      count: 0,
      isActive: false,
      laboratory: 'AU',
      connections: ['validation'],
    },
    {
      id: 'validation',
      name: 'Contrôle Qualité',
      shortName: 'VALIDATION',
      icon: <VerifiedUser fontSize="large" />,
      x: 1050,
      y: 300,
      color: '#059669',
      count: 0,
      isActive: false,
      nodeType: 'main',
      connections: ['certificate'],
    },
    {
      id: 'certificate',
      name: 'Génération Certificat',
      shortName: 'CERTIFICAT',
      icon: <CheckCircle fontSize="large" />,
      x: 1300,
      y: 300,
      color: '#3b82f6',
      count: 0,
      isActive: false,
      nodeType: 'main',
      connections: ['delivery'],
    },
    {
      id: 'delivery',
      name: 'Livraison Client',
      shortName: 'LIVRAISON',
      icon: <LocalShipping fontSize="large" />,
      x: 1550,
      y: 300,
      color: '#8b5cf6',
      count: 0,
      isActive: false,
      nodeType: 'main',
      connections: [],
    },
  ]);

  // Load real statistics
  useEffect(() => {
    const loadStatistics = async () => {
      try {
        setLoading(true);
        const stats = await samplesService.getStatistics();
        console.log('🎯 TV Dashboard - Loading statistics:', stats);
        
        if (stats) {
          setStatistics(stats);
          
          // Update network nodes with real counts
          setNetworkNodes(prev => prev.map(node => {
            let count = 0;
            let isActive = false;
            
            switch (node.id) {
              case 'reception':
                count = stats.par_statut?.RECEPTION || 0;
                break;
              case 'preparation':
                count = stats.par_statut?.PREPARATION_MECANIQUE || 0;
                break;
              case 'saa_prep':
                count = stats.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0;
                break;
              case 'saa_analysis':
                count = stats.par_statut?.LABORATOIRE_SAA_ANALYSE || 0;
                break;
              case 'icp_prep':
                count = stats.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0;
                break;
              case 'icp_analysis':
                count = stats.par_statut?.LABORATOIRE_ICP_ANALYSE || 0;
                break;
              case 'au_prep':
                count = stats.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0;
                break;
              case 'au_analysis':
                count = stats.par_statut?.LABORATOIRE_AU_ANALYSE || 0;
                break;
              case 'validation':
                count = stats.par_statut?.VALIDATION_SERVICE || 0;
                break;
              case 'certificate':
                count = stats.par_statut?.GENERATION_CERTIFICAT || 0;
                break;
              case 'delivery':
                count = stats.par_statut?.PRET || 0;
                break;
            }
            
            isActive = count > 0;
            return { ...node, count, isActive };
          }));
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ TV Dashboard - Error loading statistics:', error);
        setLoading(false);
      }
    };

    loadStatistics();
    const interval = setInterval(loadStatistics, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Particle flow simulation
  useEffect(() => {
    const createParticleFlow = () => {
      const activeConnections = [];
      
      networkNodes.forEach(node => {
        if (node.count > 0) {
          node.connections.forEach(targetId => {
            const target = networkNodes.find(n => n.id === targetId);
            if (target) {
              activeConnections.push({
                from: node,
                to: target,
                intensity: Math.min(node.count / 10, 1),
              });
            }
          });
        }
      });

      // Create flowing particles
      const newParticles: FlowingParticle[] = [];
      activeConnections.forEach((connection, index) => {
        const particleCount = Math.min(Math.ceil(connection.intensity * 3), 5);
        
        for (let i = 0; i < particleCount; i++) {
          newParticles.push({
            id: `particle-${index}-${i}-${Date.now()}`,
            fromX: connection.from.x + 90,
            fromY: connection.from.y + 70,
            toX: connection.to.x + 90,
            toY: connection.to.y + 70,
            color: connection.from.color,
            delay: i * 0.8,
          });
        }
      });

      setParticles(newParticles);
    };

    if (!loading) {
      createParticleFlow();
      const interval = setInterval(createParticleFlow, 5000);
      return () => clearInterval(interval);
    }
  }, [networkNodes, loading]);

  // Update clock
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Render flowing connections
  const renderFlowingConnections = () => {
    const connections: JSX.Element[] = [];
    
    networkNodes.forEach(node => {
      node.connections.forEach(targetId => {
        const target = networkNodes.find(n => n.id === targetId);
        if (target) {
          const connectionId = `${node.id}-${targetId}`;
          const isActive = node.isActive;
          const flowIntensity = node.count / 50; // Normalize intensity
          
          // Calculate curved path
          const startX = node.x + 90;
          const startY = node.y + 70;
          const endX = target.x + 90;
          const endY = target.y + 70;
          
          const midX = (startX + endX) / 2;
          const midY = (startY + endY) / 2;
          
          // Create beautiful curved path
          const controlX1 = startX + (midX - startX) * 0.5;
          const controlY1 = startY + (midY - startY) * 0.3;
          const controlX2 = endX - (endX - midX) * 0.5;
          const controlY2 = endY - (endY - midY) * 0.3;
          
          const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;
          
          connections.push(
            <g key={connectionId}>
              <defs>
                <linearGradient id={`gradient-${connectionId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={alpha(node.color, 0.8)} />
                  <stop offset="50%" stopColor={alpha('#fbbf24', 0.9)} />
                  <stop offset="100%" stopColor={alpha(target.color, 0.8)} />
                </linearGradient>
                {isActive && (
                  <filter id={`glow-${connectionId}`}>
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                )}
              </defs>
              <FlowPath
                d={pathData}
                stroke={`url(#gradient-${connectionId})`}
                isActive={isActive}
                flowIntensity={flowIntensity}
                filter={isActive ? `url(#glow-${connectionId})` : 'none'}
              />
            </g>
          );
        }
      });
    });
    
    return connections;
  };

  return (
    <TVContainer>
      {/* Magnificent Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={4}>
          <Box 
            sx={{
              width: 280, // Much bigger - was 200, now 280
              height: 100, // FURTHER REDUCED HEIGHT - was 140, now 100 (much shorter rectangle)
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              // Glassmorphism RECTANGULAR background - BRIGHTER
              background: 'rgba(255, 255, 255, 0.25)', // Increased from 0.15 to 0.25
              backdropFilter: 'blur(15px)',
              borderRadius: '20px', // Slightly smaller radius for shorter height
              border: '1px solid rgba(255, 255, 255, 0.4)', // Increased from 0.25 to 0.4
              boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.4)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)', // Brighter gradient
                borderRadius: '20px', // Match the container radius
                zIndex: -1,
              }
            }}
          >
            <img 
              src="/logo2.png" 
              alt="AFRILAB Logo" 
              style={{ 
                width: '70%', 
                height: '70%', 
                objectFit: 'contain',
                filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.4))' 
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h2" 
              fontWeight="bold" 
              sx={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #d97706 60%, #92400e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                letterSpacing: '0.02em',
              }}
            >
              AFRILAB
            </Typography>
            <Typography 
              variant="h5" 
              sx={{
                color: alpha('#fbbf24', 0.9),
                fontWeight: 300,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Laboratoire d'Analyse Minière
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5} mt={1}>
              <Hub sx={{ color: alpha('#fbbf24', 0.7), fontSize: 20 }} />
              <Typography 
                variant="body1" 
                sx={{
                  color: alpha('#fff', 0.8),
                  fontWeight: 400,
                }}
              >
                Flux de Production Temps Réel
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box textAlign="right">
          <Typography 
            variant="h3" 
            fontWeight="300"
            sx={{
              color: '#fbbf24',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {currentTime.toLocaleTimeString('fr-FR')}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{
              color: alpha('#fff', 0.8),
              fontWeight: 300,
              textTransform: 'capitalize',
            }}
          >
            {currentTime.toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long',
              year: 'numeric',
            })}
          </Typography>
        </Box>
      </Box>

      {/* Spectacular Network Visualization */}
      {loading ? (
        <NetworkContainer 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
          flexDirection="column"
          gap={4}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{ 
                color: '#fbbf24',
                filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.5))',
              }} 
            />
            <Typography variant="h5" color="white" fontWeight="300">
              Initialisation du réseau minier...
            </Typography>
          </Box>
          <LinearProgress 
            sx={{ 
              width: 400,
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha('#fbbf24', 0.1),
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #fbbf24, #f59e0b, #d97706)',
                borderRadius: 3,
              },
            }} 
          />
        </NetworkContainer>
      ) : (
        <NetworkContainer ref={networkRef}>
          <NetworkWrapper>
            {/* Flowing Connection Lines */}
            <ConnectionSVG>
              {renderFlowingConnections()}
            </ConnectionSVG>

            {/* Network Nodes */}
            {networkNodes.map((node) => (
              <NetworkNode
                key={node.id}
                nodeColor={node.color}
                isActive={node.isActive}
                intensity={node.count / 50}
                nodeType={node.nodeType}
                sx={{ 
                  left: node.x, 
                  top: node.y,
                  zIndex: 10,
                }}
            >
              {node.count > 0 && (
                <CountBadge badgeColor={node.color} count={node.count}>
                  {node.count}
                </CountBadge>
              )}
              
              <Avatar
                sx={{
                  bgcolor: alpha(node.color, 0.15),
                  color: node.color,
                  width: node.nodeType === 'main' ? 64 : 56,
                  height: node.nodeType === 'main' ? 64 : 56,
                  mb: 1.5,
                  border: `2px solid ${alpha(node.color, 0.3)}`,
                  boxShadow: `0 4px 16px ${alpha(node.color, 0.2)}`,
                }}
              >
                {node.icon}
              </Avatar>
              
              <Typography 
                variant="caption" 
                fontWeight="bold"
                sx={{ 
                  color: node.color,
                  textAlign: 'center',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  fontSize: node.nodeType === 'main' ? '0.8rem' : '0.75rem',
                  letterSpacing: '0.5px',
                }}
              >
                {node.shortName}
              </Typography>
              
              {node.laboratory && (
                <Chip
                  label={node.laboratory}
                  size="small"
                  sx={{
                    mt: 0.5,
                    height: 20,
                    fontSize: '11px',
                    fontWeight: 'bold',
                    bgcolor: alpha(node.color, 0.15),
                    color: node.color,
                    border: `1px solid ${alpha(node.color, 0.3)}`,
                  }}
                />
              )}
              </NetworkNode>
            ))}

            {/* Flowing Particles */}
            {particles.map(particle => (
              <FlowParticle
                key={particle.id}
                particleColor={particle.color}
                sx={{
                  left: particle.fromX,
                  top: particle.fromY,
                  animationDelay: `${particle.delay}s`,
                }}
              />
            ))}
          </NetworkWrapper>
        </NetworkContainer>
      )}

      {/* Professional Statistics Dashboard */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={3}>
          <StatsCard>
            <Avatar
              sx={{
                bgcolor: 'linear-gradient(135deg, #8b5a3c 0%, #6b4423 100%)',
                width: 56,
                height: 56,
                boxShadow: '0 4px 16px rgba(139, 90, 60, 0.3)',
              }}
            >
              <Terrain fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h3" fontWeight="bold" color="#8b5a3c">
                {statistics?.total || 0}
              </Typography>
              <Typography variant="body1" color="text.secondary" fontWeight="500">
                Échantillons Total
              </Typography>
            </Box>
          </StatsCard>
        </Grid>
        
        <Grid item xs={3}>
          <StatsCard>
            <Avatar
              sx={{
                bgcolor: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                width: 56,
                height: 56,
                boxShadow: '0 4px 16px rgba(5, 150, 105, 0.3)',
              }}
            >
              <CheckCircle fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h3" fontWeight="bold" color="#059669">
                {statistics?.termines_aujourd_hui || 0}
              </Typography>
              <Typography variant="body1" color="text.secondary" fontWeight="500">
                Terminés Aujourd'hui
              </Typography>
            </Box>
          </StatsCard>
        </Grid>
        
        <Grid item xs={3}>
          <StatsCard>
            <Avatar
              sx={{
                bgcolor: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                width: 56,
                height: 56,
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)',
              }}
            >
              <AccessTime fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h3" fontWeight="bold" color="#f59e0b">
                {statistics?.moyenne_duree_jours || 0}j
              </Typography>
              <Typography variant="body1" color="text.secondary" fontWeight="500">
                Délai Moyen
              </Typography>
            </Box>
          </StatsCard>
        </Grid>
        
        <Grid item xs={3}>
          <StatsCard>
            <Avatar
              sx={{
                bgcolor: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                width: 56,
                height: 56,
                boxShadow: '0 4px 16px rgba(220, 38, 38, 0.3)',
              }}
            >
              <Speed fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h3" fontWeight="bold" color="#dc2626">
                {statistics?.en_retard || 0}
              </Typography>
              <Typography variant="body1" color="text.secondary" fontWeight="500">
                En Retard
              </Typography>
            </Box>
          </StatsCard>
        </Grid>
      </Grid>
    </TVContainer>
  );
};

export default TVDashboard;